import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useDropzone } from 'react-dropzone';
import { view, store } from '@risingstack/react-easy-state';
import { v4 as uuid } from 'uuid';
import { prefixClassName } from 'framework/components/ui/_conf';
import Info from 'framework/components/ui/Info/Info';
import { CheckboxPrimitive } from 'framework/components/ui/FormComponents/Primitives/CheckboxPrimitive';
import { SelectPrimitive } from 'framework/components/ui/FormComponents/Primitives/SelectPrimitive';
import {
  Explorer, Table, Icon, Text, Loader, Modal, Form, TextField, Img, Button,
  FilterBar,
} from 'ui';
import config from 'config';
import { fetchData } from 'framework/api/fetch-data';
import { useModal } from 'framework/components/ui/Modal/useModal';
import { addProgressBar } from 'framework/components/ui/ProgressBar/useProgressBar';
import { useForm } from 'framework/components/ui/FormComponents/Form/useForm';
import { IconNames } from 'framework/components/ui/Icon';
import { folderTextValidator } from 'framework/components/hoc/formField/validators';
import { ExplorerSelection } from 'framework/components/ui/Explorer/types';
import { i18n } from 'i18n';
import ApplicationMonitoring from 'framework/applicationMonitoring/ApplicationMonitoring';
import { TrackEventProps } from 'framework/applicationMonitoring/interface';
import { useLocation } from 'react-router-dom';
import { removeSpace } from 'framework/utils/helpers';
import { FileFolder, FileFolderType, UploaderFileManagerProps, KebabAction } from './types';
import { FILEMANAGER_MODAL_ADD_FOLDER, FILEMANAGER_MODAL_ADD_FOLDER_FORM, MAX_FILE_SIZE } from './constants';
import { FileManagerHierarchy } from './FileManagerHierarchy';
import { useExplorerFileManager, useUploaderFileManager } from './useInternalFileManager';
import { useFileManagerContext, DeleteFunc } from './FileManagerContext';
import { deleteFileFolder } from './services';
import { FileRejectionItem, FileValidator } from './FileManagerValidation';
import { useFeatureIsOn } from '../../../../growthbook/useFeatureIsOn';
import { validators } from 'framework/components/hoc/formField';
import { Validator } from 'redux-form';

export const shouldDisableCheckbox = (isFileSelected: boolean, canSelectMore: boolean): boolean =>
  !isFileSelected && !canSelectMore;

export const shouldDisableConfirmButton = (bulkAction: string): boolean => !bulkAction;

export const calculateIndent = (row?: FileFolder): number => {
  if (!row?.parent) {
    return 0;
  }
  const indent = calculateIndent(row.parent);
  return indent + 60;
};

export const shouldTriggerMoveAction = (bulkAction: string): boolean => bulkAction === 'move';

export const shouldTriggerDeleteAction = (bulkAction: string): boolean => bulkAction === 'delete';

export const isFileType = (type: FileFolderType): boolean => type === FileFolderType.FILE;

export const isDirectoryType = (type: FileFolderType): boolean => type === FileFolderType.DIRECTORY;

export const shouldShowDeleteAction = (isEmpty?: boolean): boolean => !!isEmpty;

export const buildFileLines = (files: FileFolder[], filesPerLine: number): FileFolder[][] => {
  const lines: FileFolder[][] = [];
  for (let i = 0; i < files.length; i += filesPerLine) {
    lines.push(files.slice(i, i + filesPerLine));
  }
  return lines;
};

export const calculateFolderDepth = (reference: string): number => {
  return reference.split('/').length - 2;
};

export const selectFoldersPath = (folder: FileFolder, level: number): Array<{ id: string; level: number }> => {
  if (!folder) {
    return [];
  }
  const newLevel = level - 1;
  if (folder.parent) {
    const pathStep = selectFoldersPath(folder.parent, newLevel);
    pathStep.push({ id: folder.reference, level });
    return pathStep;
  }
  return [{ id: folder.reference, level }];
};

export const filterOnlyFiles = (items: FileFolder[]): FileFolder[] => {
  return items.filter((item) => item.type === FileFolderType.FILE);
};

export const isFolderEmpty = (folder?: FileFolder): boolean => {
  return !folder?.children?.length;
};

export const isGridMode = (mode: string): boolean => mode === 'grid';
export const isColumnsMode = (mode: string): boolean => mode === 'columns';
export const shouldClearPreviewInGridMode = (mode: string): boolean => isGridMode(mode);
export const hasCurrentFolder = (currentFolder?: FileFolder): boolean => !!currentFolder;
export const hasFolder = (folder?: FileFolder): boolean => !!folder;
export const shouldShowBulkCheckbox = (isBulkActionsEnabled: boolean, fileType: FileFolderType): boolean =>
  isBulkActionsEnabled && fileType === FileFolderType.FILE;
export const hasPreviewableFile = (previewableFile?: FileFolder): boolean => !!previewableFile;
export const isClipboardFeedbackActive = (feedback: string | boolean, fileUrl: string): boolean => feedback === fileUrl;

export const buildFilesPreviewColumnLabel = (filesCount: number): string => {
  const fileCountText = filesCount > 0 ? ` (${filesCount})` : '';
  return `${i18n.t('modules.contentmanagement.uploadableFiles')}${fileCountText}`;
};

const clx = prefixClassName('');
const cssClass = prefixClassName('file-manager');

const RETURN_KEY = 13;
const FILES_PER_LINE = 4;

export type DirectoryActionsProps = {
  isLoading: boolean;
  isEmpty?: boolean;
  handleOpenFolderModal: (root?: boolean) => () => void;
  prompDelete: (mode: FileFolderType, onDelete: DeleteFunc) => () => void;
  onActiveFolderDelete: () => void;
};

// Exported for testing - logic only
export const getDirectoryActionsLogic = (isLoading: boolean, isEmpty?: boolean) => {
  if (isLoading) {
    return { type: 'loading' as const };
  }
  return {
    type: 'actions' as const,
    includeDelete: !!isEmpty,
  };
};

const getDirectoryActions = ({
  isLoading,
  isEmpty,
  handleOpenFolderModal,
  prompDelete,
  onActiveFolderDelete,
}: DirectoryActionsProps): KebabAction[] => {
  const logic = getDirectoryActionsLogic(isLoading, isEmpty);

  if (logic.type === 'loading') {
    return [
      {
        label: <Loader size="xs" />,
        onClick: undefined,
      },
    ];
  }

  return [
    {
      label: 'modules.contentmanagement.addSubFolder',
      onClick: handleOpenFolderModal(true),
    },
    ...(logic.includeDelete
      ? [
          {
            label: 'generic.delete',
            onClick: prompDelete(FileFolderType.DIRECTORY, onActiveFolderDelete),
          },
        ]
      : []),
  ];
};

type ViewMode = 'columns' | 'grid';

type ViewModeButtonProps = {
  type: ViewMode;
  mode: ViewMode;
  onClick: React.Dispatch<React.SetStateAction<ViewMode>>;
  trackEventProps?: TrackEventProps;
};
const ViewModeButton = ({ type, mode, onClick: setMode, trackEventProps }: ViewModeButtonProps) => {
  const { pathname } = useLocation();

  const handleOnClick = () => {
    ApplicationMonitoring.trackEvent(
      trackEventProps?.name || pathname,
      trackEventProps?.value || 'Explorer view mode',
      trackEventProps?.label || type
    );

    setMode(type);
  };

  return (
    <Button
      icon={type as never}
      outline
      className={classnames(`${cssClass}__icon-mode`, { [`${cssClass}__icon-mode--disable`]: mode !== type })}
      onClick={handleOnClick}
    />
  );
};

type FileManagerExplorerProps = Pick<
  ReturnType<typeof useExplorerFileManager>,
  'store' | 'previewableFile' | 'handleExplorerRowClick' | 'openMoveModal'
> & {
  onFileDelete: (file: FileFolder) => Promise<void>;
  onActiveFolderDelete: () => void;
  onFileSelected?: (file: FileFolder) => void;
  fileSelectable: boolean;
  currentFolder?: FileFolder;
  onCurrentFolderSelect?: (folder: FileFolder) => void;
  requiredFilesFormat?: string[];
};

// Exported for testing - determines action type
export const getColumnActionType = (type: FileFolderType): 'file' | 'directory' => {
  return type === FileFolderType.FILE ? 'file' : 'directory';
};

export const getColumnActions = (
  type: FileFolderType,
  isLoading: boolean,
  isEmpty?: boolean,
  prompDelete?: any,
  handleOpenFolderModal?: any,
  onActiveFolderDelete?: any,
  openMoveModal?: any,
  row?: FileFolder,
  isSearchView?: boolean,
): Array<KebabAction> => {
  if (type === FileFolderType.FILE) {
    return [
      {
        label: 'modules.contentmanagement.moveFile',
        onClick: () => openMoveModal?.(row),
      },
    ];
  }
  // If explorer is in search view, do not allow directory actions
  if (isSearchView) return [];
  return getDirectoryActions({
    isLoading,
    isEmpty,
    prompDelete,
    handleOpenFolderModal,
    onActiveFolderDelete,
  });
};

const fileManageModeStore = store<{ mode: ViewMode }>({
  mode: 'columns'
});

export const FileManagerExplorerView = view((props: FileManagerExplorerProps) => {
  const { currentFolder, onCurrentFolderSelect, ...rest } = props;
  const { setPreviewableFile } = useFileManagerContext();
  const [localCurrentFolder, setLocalCurrentFolder] = useState<FileFolder>(props.currentFolder);

  const searchTermLengthValidator: Validator[] = React.useMemo(() => ([validators.minimumLength(4)]), []);

  const damSearchAndBulk: boolean = useFeatureIsOn('admin-dam-search-and-bulk-actions');

  useEffect(() => {
    if (shouldClearPreviewInGridMode(fileManageModeStore.mode)) {
      setPreviewableFile(undefined);
    }
  }, [fileManageModeStore.mode]);

  const onFolderChange = (folder: FileFolder) => {
    setLocalCurrentFolder(folder);
  };

  const setMode = (newMode: ViewMode) => {
    fileManageModeStore.mode = newMode;
  };

  return (
    <>
      <div className={classnames(
        `${cssClass}__filter-bar-wrapper`,
        damSearchAndBulk && `${cssClass}__filter-bar-wrapper__search-area`,
      )}>
        {
          damSearchAndBulk && 
            <FilterBar
              name="damSearch"
              initialValues={{
                damSearchTerm: props.store.searchTerm
              }}
            >
              <FilterBar.Input
                name="damSearchTerm"
                testId="damSearchFilter"
                onFilter={(term: string) => {
                  props.store.searchFiles(term);
                }}
                validate={searchTermLengthValidator}
                placeholder={i18n.t('modules.contentmanagement.filterByName')}
              />
            </FilterBar>
        }
        <div className={`${cssClass}__viewmode-buttons`}>
          <ViewModeButton type="columns" mode={fileManageModeStore.mode} onClick={setMode} />
          <ViewModeButton type="grid" mode={fileManageModeStore.mode} onClick={setMode} />
        </div>
      </div>
      {fileManageModeStore.mode === 'columns' && (
        <FileManagerExplorerColumnView
          currentFolder={localCurrentFolder}
          onCurrentFolderSelect={onFolderChange}
          {...rest}
        />
      )}

      {fileManageModeStore.mode === 'grid' && (
        <FileManagerExplorerGridView
          currentFolder={localCurrentFolder}
          onCurrentFolderSelect={onFolderChange}
          {...rest}
        />
      )}
    </>
  );
});

const FileManagerExplorerColumnView = view(
  ({
    store,
    handleExplorerRowClick,
    openMoveModal,
    previewableFile,
    onFileSelected,
    onFileDelete,
    onActiveFolderDelete,
    fileSelectable,
    onCurrentFolderSelect,
    currentFolder,
    requiredFilesFormat,
  }: FileManagerExplorerProps) => {
    const { setStore, handleOpenFolderModal, prompDelete, toggleFileSelection, isFileSelected, canSelectMore } =
      useFileManagerContext();
    const [externalSelectionArray, setExternalSelectionArray] = useState<ExplorerSelection[]>();
    const isBulkActionsEnabled = useFeatureIsOn('admin-dam-search-and-bulk-actions');

    useEffect(() => {
      setStore(store as never);
    }, []);

    useEffect(() => {
      if (hasCurrentFolder(currentFolder)) {
        const depth = calculateFolderDepth(currentFolder.reference);
        const stepPath = selectFoldersPath(currentFolder, depth);
        setExternalSelectionArray(stepPath as ExplorerSelection[]);
      }
    }, []);

    const handleExplorerRowClickIntermediary = (row: FileFolder) => {
      onCurrentFolderSelect(row);
      return handleExplorerRowClick(row);
    };

    const handleOnExplorerSelection = ({ selection, row }) => {
      setExternalSelectionArray(selection);
      handleExplorerRowClick(row);
      onCurrentFolderSelect(row);
    };

    const handleCheckboxChange = (row: FileFolder) => (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      toggleFileSelection(row);
    };

    return (
      <Explorer<FileFolder>
        data={store.data}
        idProp="reference"
        preview={
          previewableFile ? (
            <PreviewFile
              fileSelectable={fileSelectable}
              preview={previewableFile}
              isFileSelected={isFileSelected(previewableFile)}
              onPreviewSelect={() => onFileSelected?.(previewableFile)}
              onPreviewDelete={() => onFileDelete(previewableFile)}
            />
          ) : undefined
        }
        onRowClick={handleExplorerRowClickIntermediary}
        onExplorerSelection={handleOnExplorerSelection}
        onRowClass={() => `${cssClass}__row-explorer`}
        columnMetadata={['', '']}
        renderColumns={({ row, isSelected }) => [
          <Table.Column key="td-content" className={`${cssClass}__td-content`}>
            <div className={`${cssClass}__content`}>
              {isBulkActionsEnabled && row.type === FileFolderType.FILE && (
                <CheckboxPrimitive
                  value={isFileSelected(row)}
                  onChange={handleCheckboxChange(row)}
                  disabled={shouldDisableCheckbox(isFileSelected(row), canSelectMore)}
                  className={`${cssClass}__checkbox`}
                  name={`file-checkbox-${row.reference}`}
                />
              )}
              {store.isLoading(row.reference) ? (
                <Loader size="xs" />
              ) : (
                <Icon
                  name={store.resolveIcon(row)}
                  className={classnames(`${cssClass}__icon`, isSelected && `${cssClass}__icon--selected`)}
                />
              )}
              <Text tag="div" caption={row.name} className={`${cssClass}__label`} />
              <div />
            </div>
          </Table.Column>,

          <Table.ColumnKebab
            key="td-action"
            keepDropdown
            options={getColumnActions(
              row.type,
              store.isLoading(row.reference),
              isFolderEmpty(row),
              prompDelete,
              handleOpenFolderModal,
              onActiveFolderDelete,
              openMoveModal,
              row,
              store.searchTerm?.length > 0
            )}
            className={`${clx}Table-cell--no-ellipsis`}
          />,
        ]}
        externalSelection={externalSelectionArray || undefined}
        requiredFilesFormat={requiredFilesFormat}
      />
    );
  }
);

const FileManagerExplorerGridView = view(
  ({
    store,
    fileSelectable,
    handleExplorerRowClick: onExplorerRowClick,
    onFileDelete,
    onFileSelected,
    onCurrentFolderSelect,
    currentFolder,
  }: FileManagerExplorerProps) => {
    const [activeFiles, setActiveFiles] = useState<FileFolder[]>([]);
    const [activeFolder, setActiveFolder] = useState<FileFolder>(currentFolder);
    const { refreshTrigger } = useFileManagerContext();

    const handleFolderHighlight = (folder: FileFolder) => {
      if (hasFolder(folder)) {
        onExplorerRowClick(folder).then((res) => {
          setActiveFiles(filterOnlyFiles(res));
          setActiveFolder(folder);
          onCurrentFolderSelect(folder);
        });
      }
    };

    useEffect(() => {
      handleFolderHighlight(activeFolder);
    }, []);

    useEffect(() => {
      if (refreshTrigger > 0 && activeFolder) {
        handleFolderHighlight(activeFolder);
      }
    }, [refreshTrigger]);

    return (
      <div className={`${cssClass}__gridview`}>
        <div className={`${cssClass}__gridview__hierarchy`}>
          <FileManagerHierarchy
            hideHeaders
            displayEmptyRow={false}
            data={store.data}
            isLoading={store.isLoading}
            resolveIcon={store.resolveIcon}
            onHierarchyFolderHighlight={handleFolderHighlight}
            onHierarchyRowExpanding={onExplorerRowClick}
            activeFolder={activeFolder}
          />
        </div>
        <div className={`${cssClass}__gridview__gridpane`}>
          <FileManagerGridView
            files={activeFiles}
            fileSelectable={fileSelectable}
            activeFolder={activeFolder}
            onFileDelete={onFileDelete}
            onFileSelected={onFileSelected}
            refresh={handleFolderHighlight}
          />
        </div>
      </div>
    );
  }
);

type FileManagerGridViewProps = {
  files: FileFolder[];
  fileSelectable: boolean;
  activeFolder?: FileFolder;
  onFileDelete: (file: FileFolder) => Promise<void>;
  onFileSelected?: (file: FileFolder) => void;
  refresh: (row: FileFolder) => void;
};
const FileManagerGridView = view(
  ({ files, onFileDelete, refresh, activeFolder, fileSelectable, onFileSelected }: FileManagerGridViewProps) => {
    const rootClassName = `${cssClass}__gridview__gridpane__files`;
    const [clipboardCopyFeedback, setClipboardCopyFeedback] = React.useState<string | boolean>(false);
    const handleCopyToClipboard = (file: FileFolder) => {
      navigator.clipboard
        .writeText(file.url)
        .then(() => {
          setClipboardCopyFeedback(file.url);
        })
        .finally(() => {
          setTimeout(() => setClipboardCopyFeedback(false), 5000);
        });
    };

    const { prompDelete, toggleFileSelection, isFileSelected, canSelectMore, setPreviewableFile } = useFileManagerContext();
    const isBulkActionsEnabled = useFeatureIsOn('admin-dam-search-and-bulk-actions');

    const handleDeleteFile = (file: FileFolder) => () => {
      onFileDelete(file).then(() => {
        refresh(activeFolder);
      });
    };

    const handleCheckboxChange = (file: FileFolder) => (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      toggleFileSelection(file);
    };

    const lines = buildFileLines(files, FILES_PER_LINE);

    const buildRows = (lines: Array<FileFolder[]>) =>
      lines.map((line) => (
        <div key={uuid()} className={`${rootClassName}__line`}>
          {line.map((file) => (
            <div key={uuid()} className={`${rootClassName}__line__file`}>
              {isBulkActionsEnabled && (
                <div className={`${rootClassName}__line__file__checkbox`}>
                  <CheckboxPrimitive
                    value={isFileSelected(file)}
                    onChange={handleCheckboxChange(file)}
                    disabled={shouldDisableCheckbox(isFileSelected(file), canSelectMore)}
                    name={`file-checkbox-grid-${file.reference}`}
                  />
                </div>
              )}
              <div className={`${rootClassName}__line__file__image`}>
                <Img height="" width="" src={file.url} />
              </div>
              <div className={`${rootClassName}__line__file__name`}>{file.name}</div>
              <div className={`${rootClassName}__line__file__url`}>{file.reference}</div>
              <div className={`${rootClassName}__line__file__actions`}>
                <Button className={`${cssClass}__copy-to-clipboard`} onClick={() => handleCopyToClipboard(file)} link>
                  <Text caption={i18n.t('generic.copyToClipboard')} tag="span" />
                  {clipboardCopyFeedback === file.url && (
                    <Text
                      className={`${cssClass}__copy-to-clipboard__success`}
                      caption={i18n.t('generic.copyToClipboardSuccess')}
                      tag="span"
                    />
                  )}
                </Button>
                <Button
                  link
                  caption="generic.options.delete"
                  onClick={() => {
                    setPreviewableFile(file);
                    prompDelete(FileFolderType.FILE, handleDeleteFile(file))();
                  }}
                  className={`${rootClassName}__line__file__actions__delete`}
                  disabled={isFileSelected(file)}
                />
                {fileSelectable && (
                  <Button
                    caption="modules.contentmanagement.selectFile"
                    onClick={() => onFileSelected?.(file)}
                    className={`${rootClassName}__line__file__actions__select`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      ));

    const rows = buildRows(lines);

    return <div className={rootClassName}>{rows}</div>;
  }
);

export const FileManagerExplorer = view(
  ({
    store,
    handleExplorerRowClick,
    openMoveModal,
    previewableFile,
    onFileSelected,
    onFileDelete,
    onActiveFolderDelete,
    fileSelectable,
  }: FileManagerExplorerProps) => {
    const { setStore, handleOpenFolderModal, prompDelete, isFileSelected } = useFileManagerContext();

    useEffect(() => {
      setStore(store as never);
    }, []);

    return (
      <Explorer<FileFolder>
        data={store.data}
        idProp="reference"
        preview={
          previewableFile ? (
            <PreviewFile
              fileSelectable={fileSelectable}
              preview={previewableFile}
              isFileSelected={isFileSelected(previewableFile)}
              onPreviewSelect={() => onFileSelected?.(previewableFile)}
              onPreviewDelete={() => onFileDelete(previewableFile)}
            />
          ) : undefined
        }
        onRowClick={handleExplorerRowClick}
        onRowClass={() => `${cssClass}__row-explorer`}
        columnMetadata={['', '']}
        renderColumns={({ row, isSelected }) => [
          <Table.Column key="td-content" className={`${cssClass}__td-content`}>
            <div className={`${cssClass}__content`}>
              {store.isLoading(row.reference) ? (
                <Loader size="xs" />
              ) : (
                <Icon
                  name={store.resolveIcon(row)}
                  className={classnames(`${cssClass}__icon`, isSelected && `${cssClass}__icon--selected`)}
                />
              )}
              <Text tag="div" caption={row.name} className={`${cssClass}__label`} />
              <div />
            </div>
          </Table.Column>,

          <Table.ColumnKebab
            key="td-action"
            keepDropdown
            options={getColumnActions(
              row.type,
              store.isLoading(row.reference),
              isFolderEmpty(row),
              prompDelete,
              handleOpenFolderModal,
              onActiveFolderDelete,
              openMoveModal,
              row,
              false
            )}
          />,
        ]}
      />
    );
  }
);

export const FileManagerUpload = view(({ uploadableFiles: files, onDeleteFile, fileSelectable, ...rest }: UploaderFileManagerProps) => {
  const {
    store,
    handleDropFiles,
    handleHighlightFolder,
    handleRowExpanding,
  } = useUploaderFileManager({ uploadableFiles: files, fileSelectable, ...rest });

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, fileRejections } = useDropzone({
    onDrop: handleDropFiles,
    multiple: true,
    validator: FileValidator,
    maxSize: MAX_FILE_SIZE,
  });

  const { setStore, handleOpenFolderModal, prompDelete, activeFolder } = useFileManagerContext();

  useEffect(() => {
    setStore(store as never);
  }, []);

  const fileRejectionItems = React.useMemo(() => {
    return fileRejections.map((rejection) => <FileRejectionItem key={rejection.file.name} {...rejection} />);
  }, [fileRejections]);

  return (
    <div className={`${cssClass}__uploader`}>
      <div className={`${cssClass}__uploader__dropzone`}>
        <div
          className={classnames(
            `${cssClass}__uploader__base-style`,
            isDragActive && `${cssClass}__uploader__base-style--active`,
            isDragAccept && `${cssClass}__uploader__base-style--accept`,
            isDragReject && `${cssClass}__uploader__base-style--reject`
          )}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <p className={`${cssClass}__uploader__prompt`}>
            <Text caption="modules.contentmanagement.dragAndDrop" />
          </p>
        </div>
        <div className={`${cssClass}__error`}>{fileRejectionItems}</div>
        {files.length ? <FilesPreview files={files} onDeleteFile={onDeleteFile} /> : <></>}
      </div>

      {store.initializing ? (
        <div className={`${cssClass}__loader`}>
          <Loader size="lg" />
        </div>
      ) : (
        <FileManagerHierarchy
          wider
          data={store.data}
          isLoading={store.isLoading}
          resolveIcon={store.resolveIcon}
          onHierarchyRowExpanding={handleRowExpanding}
          onHierarchyFolderHighlight={handleHighlightFolder}
          onKebabActions={(row: FileFolder) =>
            getDirectoryActions({
              isEmpty: isFolderEmpty(row),
              isLoading: store.isLoading(row.reference),
              prompDelete,
              handleOpenFolderModal,
              onActiveFolderDelete: () => {
                deleteFileFolder(activeFolder.reference).then(() => {
                  store.initializeStore(true);
                });
              },
            })
          }
        />
      )}
    </div>
  );
});

type Callback<T, K = FileFolder> = (row: K) => T;
type ColumnContentProps = {
  row: FileFolder;
  isSelected: Callback<boolean>;
  isExpanded: Callback<boolean>;
  isLoading: Callback<boolean, string>;
  resolveIcon: Callback<IconNames>;
  toggleExpanded: Callback<() => void>;
  toggleSelect: Callback<{
    onClick: () => void;
  }>;
};
export const ColumnContent = ({
  row,
  isExpanded,
  isLoading,
  isSelected,
  resolveIcon,
  toggleExpanded,
  toggleSelect,
}: ColumnContentProps) => {
  return (
    <div
      style={{ marginLeft: calculateIndent(row) }}
      className={classnames(
        `${cssClass}__treeview-handle`,
        isSelected(row) && `${cssClass}__treeview-handle--selected`,
        row.parent && isExpanded(row.parent) && `${cssClass}__treeview-handle--expanded`
      )}
    >
      {row.type === FileFolderType.FILE ? (
        <Text tag="span" className={`${cssClass}__treeview--empty`} caption={row.name} />
      ) : (
        <>
          {isLoading(row.reference) ? (
            <Loader size="xs" />
          ) : (
            <Icon
              onClick={toggleExpanded(row)}
              className={classnames(`${cssClass}__treeview__arrow-icon`)}
              name="dropdownArrowRight"
              rotate={isExpanded(row) ? 90 : null}
            />
          )}
          <Icon name={resolveIcon(row)} className={classnames(`${cssClass}__icon`)} />
          <div {...toggleSelect(row)}>
            <Text tag="span" caption={row.name} />
          </div>
        </>
      )}
    </div>
  );
};

type FilePreviewProps = {
  files: File[];
  onDeleteFile: (file: File) => void;
};
const FilesPreview = ({ files, onDeleteFile }: FilePreviewProps) => (
  <Table
    noBorder
    data={files}
    className={`${cssClass}__files-preview`}
    columnMetadata={[
      buildFilesPreviewColumnLabel(files.length),
      'modules.contentmanagement.fileSize', 
      '',
    ]}
    cellRenderer={(_, file: File) => (
      <Table.DeletableRow>
        <Table.ColumnText caption={file.name} />
        <Table.ColumnText caption={`${file.size}`} />
        <Table.ColumnDelete onDelete={() => onDeleteFile(file)} />
      </Table.DeletableRow>
    )}
  />
);

type NewDirectoryName = {
  newDirectoryName: string;
};
type FileManagerAddFolderProps = {
  parentFolder?: string;
  onFolderCreated: () => void;
};
export const FileManagerAddFolder = ({ parentFolder, onFolderCreated }: FileManagerAddFolderProps) => {
  const { submit } = useForm(FILEMANAGER_MODAL_ADD_FOLDER_FORM);
  const { closeModal } = useModal(FILEMANAGER_MODAL_ADD_FOLDER);

  const addFolder = addProgressBar(async ({ newDirectoryName }: NewDirectoryName) => {
    const modifiedNewDirectoryName = removeSpace(newDirectoryName, '-');
    const res = await fetchData({
      url: `${config.apiEndpoints.cms}/dam/${parentFolder ?? ''}`,
      method: 'POST',
      body: {
        newDirectoryName: modifiedNewDirectoryName,
      },
    });

    if (res.success) {
      closeModal();
      onFolderCreated();
    }
    return res;
  });

  return (
    <Modal
      size="small"
      name={FILEMANAGER_MODAL_ADD_FOLDER}
      caption={`modules.contentmanagement.${parentFolder ? 'addSubFolder' : 'addFolder'}`}
      closeOnUnmount
      footerOptions={[{ caption: 'generic.button.save', formToSubmit: FILEMANAGER_MODAL_ADD_FOLDER_FORM }]}
    >
      <Form
        name={FILEMANAGER_MODAL_ADD_FOLDER_FORM}
        onSubmitHandler={addFolder}
        mapErrorsToFields={(errors: { newDirectoryName: string[] }) => ({
          newDirectoryName: errors.newDirectoryName.length
            ? errors.newDirectoryName[0]
            : 'generic.formsValidation.formSubmissionFailed',
        })}
      >
        <TextField
          name="newDirectoryName"
          caption="modules.contentmanagement.folderName"
          placeholder="modules.contentmanagement.enterFolderName"
          grid
          validate={[folderTextValidator]}
          onKeyPress={(e) => {
            if (e.which === RETURN_KEY || e.charCode === RETURN_KEY) {
              e.preventDefault();
              submit();
            }
          }}
        />
      </Form>
    </Modal>
  );
};

export type BulkActionsBarProps = {
  selectedFiles: FileFolder[];
  onClearSelection: () => void;
  onMoveFiles: () => void;
  bulkAction: string;
  setBulkAction: (action: string) => void;
  onDeleteFiles: () => void;
};

export const BulkActionsBar = ({
  selectedFiles,
  onClearSelection,
  onMoveFiles,
  bulkAction,
  setBulkAction,
  onDeleteFiles,
}: BulkActionsBarProps) => {
  const handleActionChange = (selectedOption: { value: string; label: string }) => {
    setBulkAction(selectedOption?.value || '');
  };

  const handleConfirm = () => {
    if (shouldTriggerMoveAction(bulkAction)) {
      onMoveFiles();
    }
    if (shouldTriggerDeleteAction(bulkAction)) {
      onDeleteFiles();
    }
  };

  const tooltipContent = (
    <div className={`${cssClass}__bulk-actions__tooltip-content`}>
      <div className={`${cssClass}__bulk-actions__tooltip-title`}>
        <strong>
          {String(i18n.t('modules.contentmanagement.selectedFilesCount', { count: selectedFiles.length }))}
        </strong>
      </div>
      <div className={`${cssClass}__bulk-actions__tooltip-list`}>
        {selectedFiles.map((file) => (
          <div key={file.reference} className={`${cssClass}__bulk-actions__tooltip-file`}>
            {file.reference}
          </div>
        ))}
      </div>
    </div>
  );

  const selectOptions = [
    { value: 'move', label: i18n.t('modules.contentmanagement.moveFilesToFolder') },
    { value: 'delete', label: i18n.t('modules.contentmanagement.deleteFiles') },
  ];

  return (
    <div className={`${cssClass}__bulk-actions`}>
      <div className={`${cssClass}__bulk-actions__info`}>
        <Text
          tag="span"
          caption={i18n.t('modules.contentmanagement.filesSelected', { count: selectedFiles.length })}
          className={`${cssClass}__bulk-actions__count`}
        />
        <Info
          showOnHover
          toolTipPosition="down-right"
          tooltipBody={tooltipContent}
          className={`${cssClass}__bulk-actions__info-icon`}
        />
      </div>
      <SelectPrimitive
        name="bulkAction"
        value={bulkAction}
        onChange={handleActionChange}
        className={`${cssClass}__bulk-actions__select`}
        placeholder={i18n.t('modules.contentmanagement.selectAction')}
        options={selectOptions}
        multi={false}
        searchable={false}
      />
      <Button
        caption="generic.button.confirm"
        onClick={handleConfirm}
        disabled={shouldDisableConfirmButton(bulkAction)}
        className={`${cssClass}__bulk-actions__confirm`}
      />
      <Button
        caption="generic.button.cancel"
        outline
        onClick={onClearSelection}
        className={`${cssClass}__bulk-actions__cancel`}
      />
    </div>
  );
};

type PreviewProps = {
  preview: FileFolder;
  fileSelectable: boolean;
  isFileSelected: boolean;
  onPreviewSelect?: () => void;
  onPreviewDelete: () => void;
};

const PreviewFile = ({ preview, fileSelectable, isFileSelected = false, onPreviewSelect, onPreviewDelete }: PreviewProps) => {
  const { prompDelete } = useFileManagerContext();
  const [clipboardCopyFeedback, setClipboardCopyFeedback] = React.useState<boolean>(false);
  const handleCopyToClipboard = () => {
    navigator.clipboard
      .writeText(preview.url)
      .then(() => {
        setClipboardCopyFeedback(true);
      })
      .finally(() => {
        setTimeout(() => setClipboardCopyFeedback(false), 5000);
      });
  };
  return (
    <div className={`${cssClass}__preview`}>
      <div className={`${cssClass}__preview-content`}>
        <Img className={`${cssClass}__preview-img`} src={preview.url} height="" width="" />
      </div>
      <div className={`${cssClass}__preview-info`}>
        <Text caption={preview.name} className={`${cssClass}__preview-filename`} tag="span" />
        <Text caption={preview.reference} className={`${cssClass}__preview-reference`} tag="span" />
        <Button className={`${cssClass}__copy-to-clipboard`} onClick={handleCopyToClipboard} link>
          <Text caption={i18n.t('generic.copyToClipboard')} tag="span" />
          {clipboardCopyFeedback && (
            <Text
              className={`${cssClass}__copy-to-clipboard__success`}
              caption={i18n.t('generic.copyToClipboardSuccess')}
              tag="span"
            />
          )}
        </Button>
        <Button
          className={`${cssClass}__preview-delete`}
          caption="generic.delete"
          link
          onClick={prompDelete(FileFolderType.FILE, onPreviewDelete)}
          // Disable single delete button if file is selected for a bulk action
          disabled={isFileSelected}
        />
        {fileSelectable && (
          <div className={`${cssClass}__preview-select`}>
            <Button caption="modules.contentmanagement.selectFile" onClick={onPreviewSelect} />
          </div>
        )}
      </div>
    </div>
  );
};
